import { useMemo, type ReactNode } from "react";
import { markdownLesen, type Block, type Inline } from "@stammbaum/shared";

/**
 * Zeigt eine Notiz oder Transkription an.
 *
 * Es gibt hier bewusst kein `dangerouslySetInnerHTML` und keine
 * Säuberung: `markdownLesen` liefert einen Baum, und daraus werden
 * React-Elemente. React setzt Text immer als Text ein. Ein `<script>` in
 * einer Notiz bleibt also sichtbarer Text, und zwar ohne dass irgendwo
 * eine Filterliste gepflegt werden muss, die eines Tages eine Lücke hat.
 *
 * Welche Zeichen erkannt werden, steht in packages/shared/src/markdown.ts.
 */
export function Markdown({
  text,
  klasse = "space-y-2",
}: {
  text?: string | null;
  klasse?: string;
}) {
  const bloecke = useMemo(() => markdownLesen(text), [text]);
  if (bloecke.length === 0) return null;
  return <div className={klasse}>{bloecke.map((b, i) => blockBauen(b, i))}</div>;
}

function inlineBauen(knoten: Inline[]): ReactNode[] {
  /*
   * Der Index als Schlüssel ist hier in Ordnung: die Liste entsteht neu,
   * sobald sich der Text ändert, und sie wird nie umsortiert oder
   * teilweise ersetzt.
   */
  return knoten.map((k, i) => {
    switch (k.art) {
      case "text":
        return <span key={i}>{k.text}</span>;
      case "umbruch":
        return <br key={i} />;
      case "fett":
        return <strong key={i}>{inlineBauen(k.kinder)}</strong>;
      case "kursiv":
        return <em key={i}>{inlineBauen(k.kinder)}</em>;
      case "woertlich":
        return (
          <code key={i} className="rounded bg-black/[0.06] px-1 text-[0.9em] dark:bg-white/10">
            {k.text}
          </code>
        );
      case "link":
        return (
          // Dieselbe Form wie bei den Quellenverweisen in der
          // Ereignisliste, samt Pfeil: ein Link, der das Fenster wechselt,
          // soll das vorher sagen.
          <a
            key={i}
            href={k.url}
            target="_blank"
            rel="noopener noreferrer"
            title={k.url}
            className="underline hover:text-black dark:hover:text-white"
          >
            {inlineBauen(k.kinder)}
            <span aria-hidden> ↗</span>
          </a>
        );
    }
  });
}

function blockBauen(b: Block, i: number): ReactNode {
  switch (b.art) {
    case "absatz":
      return <p key={i}>{inlineBauen(b.inhalt)}</p>;

    case "ueberschrift": {
      // Keine h1/h2/h3: die Notiz steht in einer Seite, die ihre eigene
      // Gliederung hat, und eine fremde h1 mittendrin macht die
      // Vorlesereihenfolge kaputt. Die Stufe steuert nur die Groesse.
      const groesse =
        b.stufe === 1 ? "text-base font-semibold" : b.stufe === 2 ? "font-semibold" : "font-medium";
      return (
        <p key={i} className={`${groesse} mt-3 first:mt-0`}>
          {inlineBauen(b.inhalt)}
        </p>
      );
    }

    case "zitat":
      return (
        <blockquote
          key={i}
          className="border-l-2 border-black/15 pl-3 text-black/70 dark:border-white/20
                     dark:text-white/70"
        >
          <div className="space-y-2">{b.kinder.map((k, j) => blockBauen(k, j))}</div>
        </blockquote>
      );

    case "liste":
      return b.nummeriert ? (
        <ol key={i} className="list-decimal space-y-0.5 pl-5">
          {b.punkte.map((p, j) => (
            <li key={j}>{inlineBauen(p)}</li>
          ))}
        </ol>
      ) : (
        <ul key={i} className="list-disc space-y-0.5 pl-5">
          {b.punkte.map((p, j) => (
            <li key={j}>{inlineBauen(p)}</li>
          ))}
        </ul>
      );

    case "tabelle": {
      const stellung = (spalte: number) => {
        const a = b.ausrichtung[spalte];
        return a === "rechts" ? "text-right" : a === "mitte" ? "text-center" : "text-left";
      };
      const zelle = "border-b border-black/10 py-1.5 pr-4 align-top dark:border-white/10";
      return (
        // Waagrecht scrollbar: eine Tabelle mit vier Spalten passt am
        // Handy nicht in die Breite, und abgeschnittene Lesungen waeren
        // schlimmer als eine Leiste zum Schieben.
        <div key={i} className="overflow-x-auto">
          <table className="w-full border-collapse">
            {b.kopf.length > 0 && (
              <thead>
                <tr>
                  {b.kopf.map((k, j) => (
                    <th
                      key={j}
                      className={`${zelle} ${stellung(j)} font-semibold text-black/60 dark:text-white/60`}
                    >
                      {inlineBauen(k)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {b.zeilen.map((zeile, j) => (
                <tr key={j}>
                  {zeile.map((z, k) => (
                    <td key={k} className={`${zelle} ${stellung(k)}`}>
                      {inlineBauen(z)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "trenner":
      return <hr key={i} className="border-black/10 dark:border-white/10" />;
  }
}
